import moment from 'moment';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import { Link } from 'react-router-dom';
import { ReactNode, useEffect, useState } from 'react';
import Iconify from 'src/components/Iconify';
import { getAge } from 'src/utils/formatTime';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { Box, Card, CardProps, Collapse, IconButton, Stack, Typography } from '@mui/material';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import useScores from './scores/useScores';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import ProviderWindow from 'src/careflow/common/ProviderWindow';
import IcomxInlineTelecomAction from 'src/sections/crs/patient/components/icomx/IcomxInlineTelecomAction';

interface Props extends CardProps {
  title?: string;
  patient: WrappedPatient;
  openCollapseExternal: boolean;
}

export default function PatientSummary({ title, patient, openCollapseExternal }: Props) {
  const user = useAuth();
  const { componentsData, configurations } = useTenantConfigData();
  const { i18n } = useLocales();

  const [userId, setUserId] = useState<string>('');
  const [fhirUserId, setFhirUserId] = useState<string>('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(openCollapseExternal);
  }, [openCollapseExternal]);

  useEffect(() => {
    const currentUser = user.getCurrentUser();
    if (currentUser.id === undefined) return;
    setUserId(currentUser.id);
    if (currentUser.user_fhir_uri === undefined) return;
    setFhirUserId(currentUser.user_fhir_uri);
  }, [user]);

  return (
    <Card sx={{ p: 2, display: 'block' }}>
      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
        <Typography sx={{ mt: 1, ml: 1 }}>{title}</Typography>
        <IconButton onClick={() => setOpen(!open)}>
          <Iconify icon={open ? 'eva:arrow-ios-upward-fill' : 'eva:arrow-ios-downward-fill'} />
        </IconButton>
      </Box>

      <Collapse in={open}>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
            <Link to={PATH_DASHBOARD.crs.patient.details.forId(patient?.id!)} target="_blank">
              {i18n('patients.details.title', 'crs')}
            </Link>
            <ProviderWindow
              userId={userId}
              fhirUserId={fhirUserId}
              patientId={patient?.id ?? null}
              patientMiNTIdentifier={true}
              patientData={true}
            />
          </Stack>
        </Stack>
        <Stack>
          <PatientItem key={patient.id} patient={patient} configs={{ componentsData, configurations }} />
        </Stack>
      </Collapse>
    </Card>
  );
}

type PatientItemProps = {
  patient: WrappedPatient;
  configs: any
};

function PatientItem({ patient, configs }: PatientItemProps) {
  const { i18n } = useLocales();
  const { componentsData, configurations } = configs;
  const { sil } = useScores({ patientId: patient?.id, configurations: configurations?.fhir });
  const { showSILInformation } = componentsData ?? {};

  return (
    <Stack spacing={2} sx={{ m: 2 }}>
      <PatientSummaryRow label={i18n('patients.mrn', 'crs')} value={patient?.getMRN(configurations?.mpi?.systemMrn)?.value} />
      <PatientSummaryRow
        label={i18n('referral.details.patientInformation.name', 'crs')}
        value={patient?.getFullName()}
      />
      <PatientSummaryRow
        label={i18n('patients.dateOfBirth', 'crs')}
        value={
          patient?.birthDate
            ? `${moment.utc(new Date(patient?.birthDate)).format('MMM DD, YYYY')} (${getAge(new Date(patient?.birthDate))} years)`
            : undefined
        }
      />
      <PatientSummaryRow
        label={i18n('patients.details.personalDetails.ageGroup', 'crs')}
        value={patient?.getAgeGroup()}
        valueSx={{ textTransform: 'capitalize' }}
      />
      <PatientSummaryRow
        label={i18n('patients.phone', 'crs')}
        value={patient?.getPrimaryPhone()?.value}
        endAdornment={
          <IcomxInlineTelecomAction
            resourceType="Patient"
            resourceId={patient?.id}
            system="phone"
            tooltip="Open patient phone in iComx"
            value={patient?.getPrimaryPhone()?.value}
          />
        }
      />
      <PatientSummaryRow
        label={i18n('patients.email', 'crs')}
        value={patient?.getPrimaryEmail()?.value}
        endAdornment={
          <IcomxInlineTelecomAction
            resourceType="Patient"
            resourceId={patient?.id}
            system="email"
            tooltip="Open patient email in iComx"
            value={patient?.getPrimaryEmail()?.value}
          />
        }
      />
      <PatientSummaryRow
        label={i18n('referral.details.patientInformation.address', 'crs')}
        value={patient?.getPrimaryAddressFormatted()}
      />
      <PatientSummaryRow
        label={i18n('patients.gender', 'crs')}
        value={patient?.gender}
        valueSx={{ textTransform: 'capitalize' }}
      />
      <PatientSummaryRow
        label={i18n('patients.details.personalDetails.race', 'crs')}
        value={patient?.getRace(configurations?.fhir)?.display}
      />
      <PatientSummaryRow
        label={i18n('patients.details.personalDetails.ethnicity', 'crs')}
        value={patient?.getEthnicity(configurations?.fhir)?.display}
      />
      {!!sil && !!showSILInformation && (
        <PatientSummaryRow
          label={i18n('patients.sil', 'crs')}
          value={sil.getValue() ?? ''}
        />
      )}
    </Stack>
  );
}

interface PatientSummaryRowProps {
  label: string;
  value: ReactNode;
  endAdornment?: ReactNode;
  valueSx?: Record<string, string>;
}

function PatientSummaryRow({ label, value, endAdornment, valueSx }: PatientSummaryRowProps) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
      <Typography variant="body2">{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            textAlign: 'right',
            ...valueSx,
          }}
        >
          {value}
        </Typography>
        {endAdornment}
      </Box>
    </Stack>
  );
}
